import { useEffect, useRef, useState } from 'react';
import { ApiError, apiClient } from '../../lib/api';
import { PageStage } from '../../components/PageStage';
import { SectionHead } from '../../components/SectionHead';
import { Button } from '../../components/Button';
import { toast } from '../../lib/toast';
import styles from './SpeechCoach.module.css';

interface FillerCount {
  word: string;
  count: number;
}

interface PauseEvent {
  at_sec: number;
  gap_sec: number;
  before: string;
  after: string;
}

interface SpeechAnalysisResponse {
  transcript: string;
  duration_sec: number;
  word_count: number;
  wpm: number;
  fillers: FillerCount[];
  filler_total: number;
  filler_pct: number;
  hesitations_count: number;
  long_pauses: PauseEvent[];
  avg_sentence_length: number;
  max_sentence_length: number;
  commentary: string;
}

type Stage = 'idle' | 'recording' | 'analyzing' | 'result';

export function SpeechCoachPage() {
  const [stage, setStage] = useState<Stage>('idle');
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState<SpeechAnalysisResponse | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  function cleanup() {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop();
      } catch {
        /* noop */
      }
    }
    recorderRef.current = null;
    chunksRef.current = [];
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }

  async function startRecording() {
    setError(null);
    setResult(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMimeType();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: rec.mimeType || 'audio/webm',
        });
        analyze(blob, rec.mimeType || 'audio/webm');
      };
      rec.start();
      recorderRef.current = rec;
      setElapsed(0);
      setStage('recording');
      timerRef.current = window.setInterval(() => {
        setElapsed((e) => e + 1);
      }, 1000);
    } catch (e) {
      setError(
        (e as Error).message ||
          'Браузер не дал доступ к микрофону. Проверь разрешения сайта.',
      );
    }
  }

  function stopRecording() {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setStage('analyzing');
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.stop();
    }
    // Stream gets stopped in `onstop` cleanup → analyze.
  }

  function cancelRecording() {
    cleanup();
    setStage('idle');
    setElapsed(0);
  }

  async function analyze(blob: Blob, mime: string) {
    const ext = mimeToExt(mime);
    const file = new File([blob], `speech.${ext}`, { type: mime });
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await apiClient.post<SpeechAnalysisResponse>(
        '/api/v1/voice/speech-analysis',
        fd,
      );
      setResult(r);
      setStage('result');
    } catch (e) {
      const msg =
        e instanceof ApiError ? e.message : (e as Error).message || 'Анализ не удался';
      setError(msg);
      setStage('idle');
      toast.error(msg);
    } finally {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      recorderRef.current = null;
    }
  }

  async function uploadFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);
    setResult(null);
    setStage('analyzing');
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await apiClient.post<SpeechAnalysisResponse>(
        '/api/v1/voice/speech-analysis',
        fd,
      );
      setResult(r);
      setStage('result');
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : (err as Error).message || 'Анализ не удался';
      setError(msg);
      setStage('idle');
      toast.error(msg);
    }
  }

  if (stage === 'recording') {
    return (
      <PageStage>
        <SectionHead
          eyebrow="Тренер · Запись"
          title="Говори"
          italic="как на встрече"
          sub="Жми квадрат, когда закончил. Можно репетировать опенер, ответ на сложный вопрос или просто рассказ о себе."
        />
        <div className={styles.recorder}>
          <button className={styles.stopBtn} onClick={stopRecording}>
            <span className={styles.stopGlyph}>■</span>
          </button>
          <div className={styles.timer}>{formatTime(elapsed)}</div>
          <button className={styles.cancelLink} onClick={cancelRecording}>
            ОТМЕНИТЬ
          </button>
        </div>
      </PageStage>
    );
  }

  if (stage === 'analyzing') {
    return (
      <PageStage>
        <SectionHead
          eyebrow="Тренер · Разбор"
          title="Разбираю"
          italic="речь"
          sub="Whisper транскрибирует, потом считаются метрики и ИИ пишет разбор. Обычно 10–30 секунд."
        />
        <p className={styles.muted}>· · ·</p>
      </PageStage>
    );
  }

  if (stage === 'result' && result) {
    return (
      <PageStage>
        <SectionHead
          eyebrow="Тренер · Разбор"
          title="Готово"
          italic="что получилось"
          sub={null}
        />

        <div className={styles.metricsCard}>
          <div className={styles.metricsLabel}>Метрики</div>
          <MetricLine label="Длительность" value={formatTime(Math.round(result.duration_sec))} />
          <MetricLine
            label="Темп"
            value={`${Math.round(result.wpm)} wpm ${tempoHint(result.wpm)}`}
          />
          <MetricLine
            label="Паразиты"
            value={`${result.filler_total} (${result.filler_pct}% речи)`}
          />
          <MetricLine label="Затупы > 0.8 с" value={`${result.hesitations_count}`} />
          <MetricLine
            label="Предложения"
            value={`ср. ${result.avg_sentence_length} · макс ${result.max_sentence_length}`}
          />
        </div>

        {result.fillers.length > 0 && (
          <>
            <h3 className={styles.h3}>Паразиты</h3>
            <ul className={styles.fillerList}>
              {result.fillers.slice(0, 10).map((f) => (
                <li key={f.word} className={styles.fillerRow}>
                  <span className={styles.fillerWord}>«{f.word}»</span>
                  <span className={styles.fillerCount}>× {f.count}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {result.long_pauses.length > 0 && (
          <>
            <h3 className={styles.h3}>Длинные паузы</h3>
            <ul className={styles.pauseList}>
              {result.long_pauses.map((p, idx) => (
                <li key={idx} className={styles.pauseRow}>
                  <div className={styles.pauseHead}>
                    <span className={styles.pauseStamp}>{stampLabel(p.at_sec)}</span>
                    <span className={styles.pauseGap}>{p.gap_sec} с</span>
                  </div>
                  {(p.before || p.after) && (
                    <div className={styles.pauseQuote}>
                      «{p.before.trim()} … {p.after.trim()}»
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}

        {result.commentary && (
          <>
            <h3 className={styles.h3}>Разбор</h3>
            <div className={styles.commentary}>{result.commentary}</div>
          </>
        )}

        {result.transcript && (
          <details className={styles.transcriptToggle}>
            <summary>Транскрипт</summary>
            <p className={styles.transcript}>{result.transcript}</p>
          </details>
        )}

        <div className={styles.actions}>
          <Button
            onClick={() => {
              setResult(null);
              setStage('idle');
              setElapsed(0);
            }}
            trailing="↻"
          >
            ещё одна запись
          </Button>
        </div>
      </PageStage>
    );
  }

  // Idle
  return (
    <PageStage>
      <SectionHead
        eyebrow="Тренер"
        title="Разбор"
        italic="речи"
        sub="Запиши 1–10 минут — репетицию опенера, рассказ о себе, ответ на сложный вопрос. ИИ посчитает темп, паразиты, затупы, длину фраз и напишет разбор."
      />
      <div className={styles.specsCard}>
        <div className={styles.specsLabel}>Что меряем</div>
        <ul className={styles.specsList}>
          <li>Темп — слов в минуту (норма 130–160)</li>
          <li>Слова-паразиты — ну, э-э, короче, как бы, типа</li>
          <li>Затупы — паузы дольше 0.8 секунды</li>
          <li>Длинные паузы — топ-10 с привязкой к секундам</li>
          <li>Длина предложений — средняя и максимум</li>
        </ul>
      </div>
      {error && <div className={styles.error}>{error}</div>}
      <div className={styles.actions}>
        <Button onClick={startRecording} trailing="●">
          начать запись
        </Button>
        <label className={styles.uploadLink}>
          <input
            type="file"
            accept="audio/*"
            style={{ display: 'none' }}
            onChange={uploadFile}
          />
          ИЛИ ЗАГРУЗИТЬ ФАЙЛ
        </label>
      </div>
    </PageStage>
  );
}

function MetricLine({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.metricRow}>
      <span className={styles.metricLabel}>{label}</span>
      <span className={styles.metricValue}>{value}</span>
    </div>
  );
}

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function stampLabel(at: number): string {
  return formatTime(Math.round(at));
}

function tempoHint(wpm: number): string {
  if (wpm < 1) return '';
  if (wpm < 110) return '(вяло)';
  if (wpm < 130) return '(спокойно)';
  if (wpm <= 160) return '(норма)';
  if (wpm <= 185) return '(быстро)';
  return '(тараторишь)';
}

function pickMimeType(): string | null {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) {
      return c;
    }
  }
  return null;
}

function mimeToExt(mime: string): string {
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mp4')) return 'm4a';
  if (mime.includes('mpeg')) return 'mp3';
  if (mime.includes('wav')) return 'wav';
  return 'webm';
}
